import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, ArrowUpRight, Loader2, Mail, Download, FileDown, Package } from "lucide-react";
import { API, useAuth } from "@/lib/auth";
import { useCart } from "@/lib/cart";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const timer = useRef(null);
  const cleared = useRef(false);
  const { clear } = useCart();
  const { user, refresh } = useAuth();

  useEffect(() => {
    if (!sessionId) return;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/payments/status/${sessionId}`);
        setStatus(r.data);
        if (r.data.payment_status === "paid") {
          if (!cleared.current && r.data.kind === "shop") {
            clear();
            cleared.current = true;
          }
          // If a guest was auto-registered, prompt them to check their email
          if (!user) await refresh();
          return;
        }
        if (["failed", "expired"].includes(r.data.payment_status)) return;
        if (attempts < 60) {
          timer.current = setTimeout(() => setAttempts((a) => a + 1), 3000);
        }
      } catch {
        if (attempts < 60) {
          timer.current = setTimeout(() => setAttempts((a) => a + 1), 3000);
        }
      }
    };
    poll();
    return () => clearTimeout(timer.current);
  }, [sessionId, attempts, clear, user, refresh]);

  const paid = status?.payment_status === "paid";
  const isShop = status?.kind === "shop";

  return (
    <div data-testid="page-payment-success" className="pt-40 pb-24 min-h-[70vh]">
      <div className="mx-auto max-w-[720px] px-6 text-center">
        {paid ? (
          <>
            <div className="w-20 h-20 rounded-full bg-blush mx-auto flex items-center justify-center">
              <CheckCircle2 size={40} className="text-[hsl(var(--onyx))]" />
            </div>
            <div className="overline mt-8">
              {isShop ? `Order #${status.order_number}` : "Payment complete"}
            </div>
            <h1 className="font-display text-5xl sm:text-6xl mt-3 tracking-tight text-balance">
              {isShop ? (
                <>Thank you — <span className="italic font-light">your order is confirmed.</span></>
              ) : (
                <>Welcome to <span className="italic font-light">{status.course_name}.</span></>
              )}
            </h1>
            <p className="mt-6 text-[hsl(var(--onyx)/0.65)]">
              {isShop
                ? "We've sent a confirmation to your email. Physical items ship within 3–5 working days; digital items are already in your library."
                : "A confirmation is on its way. Your course is unlocked in your account library."}
            </p>

            {status.essentials_bundle_grant_token && (
              <EssentialsBundleDownloads token={status.essentials_bundle_grant_token} />
            )}

            {status.selections && status.selections.length > 0 && (
              <div
                data-testid="success-selections"
                className="mt-6 inline-flex flex-wrap gap-2 justify-center max-w-md"
              >
                <div className="w-full text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">
                  Your chosen methods
                </div>
                {status.selections.map((s) => (
                  <span
                    key={s}
                    className="rounded-full bg-blush border border-[hsl(var(--onyx)/0.08)] px-4 py-1.5 text-sm text-[hsl(var(--onyx))]"
                  >
                    {s}
                  </span>
                ))}
              </div>
            )}

            {!user && status.magic_url && (
              <div className="mt-8 rounded-3xl bg-blush p-6 max-w-md mx-auto text-left">
                <div className="flex items-center gap-2 text-[hsl(var(--onyx))]">
                  <Mail size={16} />
                  <span className="text-xs uppercase tracking-widest">Access your account</span>
                </div>
                <p className="mt-3 text-sm text-[hsl(var(--onyx)/0.7)] leading-relaxed">
                  We've sent a magic-link to <strong>{status.user_email}</strong>.
                  If it doesn't arrive within a minute, tap the button below —
                  it does the same thing.
                </p>
                <a
                  href={status.magic_url}
                  data-testid="success-magic-link"
                  className="btn-pill btn-primary w-full mt-4 justify-center"
                >
                  Open my account <ArrowUpRight size={16} />
                </a>
                <p className="mt-3 text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.5)]">
                  Single-use · expires in 30 min
                </p>
              </div>
            )}

            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              {user ? (
                <Link to="/account" data-testid="success-account" className="btn-pill btn-primary">
                  Go to my account <ArrowUpRight size={16} />
                </Link>
              ) : (
                <Link to="/" data-testid="success-home" className="btn-pill btn-primary">
                  Back to home <ArrowUpRight size={16} />
                </Link>
              )}
              <Link to={isShop ? "/shop" : "/academy"} className="btn-pill btn-outline">
                Continue exploring
              </Link>
            </div>

            {status?.amount && (
              <div className="mt-10 text-xs text-[hsl(var(--onyx)/0.5)] uppercase tracking-widest">
                Paid · {status.currency?.toUpperCase()} {Number(status.amount).toFixed(2)}
              </div>
            )}

            {status?.bundle_offer && (
              <div className="mt-12 rounded-[2rem] bg-blush-soft p-8 max-w-md mx-auto text-left">
                <div className="overline">Complete your kit</div>
                <h3 className="font-display text-2xl mt-2">
                  {status.bundle_offer.percent_off}% off the NøoksProRange
                </h3>
                <p className="mt-3 text-sm text-[hsl(var(--onyx)/0.65)]">
                  {status.bundle_offer.description}
                </p>
                <div className="mt-4 rounded-full bg-white px-4 py-2 inline-flex items-center gap-2 font-mono text-sm">
                  Code:
                  <span
                    data-testid="bundle-offer-code"
                    className="font-medium text-[hsl(var(--onyx))]"
                  >
                    {status.bundle_offer.code}
                  </span>
                </div>
                <Link
                  to="/shop"
                  data-testid="bundle-offer-shop"
                  className="btn-pill btn-primary mt-6 justify-center"
                >
                  Shop now with {status.bundle_offer.percent_off}% off <ArrowUpRight size={16} />
                </Link>
              </div>
            )}
          </>
        ) : (
          <>
            <Loader2 size={32} className="animate-spin mx-auto text-[hsl(var(--gold))]" />
            <div className="overline mt-6">Confirming your payment</div>
            <h1 className="font-display text-4xl sm:text-5xl mt-3 tracking-tight">
              One moment please…
            </h1>
            <p className="mt-6 text-[hsl(var(--onyx)/0.6)]">
              We're finalising with Stripe. This usually takes a few seconds.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function EssentialsBundleDownloads({ token }) {
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/downloads/${token}`)
      .then((r) => setMeta(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Could not load your downloads"));
  }, [token]);

  if (error) {
    return (
      <div
        data-testid="bundle-downloads-error"
        className="mt-10 rounded-3xl bg-blush-soft p-6 max-w-xl mx-auto text-left border border-[hsl(var(--onyx)/0.08)]"
      >
        <div className="text-sm text-[hsl(var(--onyx)/0.8)]">{error}</div>
      </div>
    );
  }
  if (!meta) {
    return (
      <div className="mt-10 flex justify-center">
        <Loader2 size={22} className="animate-spin text-[hsl(var(--burgundy))]" />
      </div>
    );
  }

  const zipHref = `${API}/downloads/${token}/zip`;

  return (
    <div
      data-testid="bundle-downloads"
      className="mt-12 rounded-[2rem] bg-white p-8 sm:p-10 max-w-[720px] mx-auto text-left border border-[hsl(var(--onyx)/0.08)] shadow-[0_20px_60px_-30px_rgba(20,10,15,0.15)]"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-blush flex items-center justify-center">
          <Package size={18} className="text-[hsl(var(--burgundy))]" />
        </div>
        <div>
          <div className="overline">Download your bundle</div>
          <div className="font-display text-2xl mt-1">Hair Extension Essentials — 5 PDFs</div>
        </div>
      </div>

      <ul className="mt-6 space-y-2.5">
        {meta.files.map((f) => (
          <li
            key={f.key}
            className="flex items-center justify-between gap-3 rounded-2xl border border-[hsl(var(--onyx)/0.08)] px-4 py-3 hover:border-[hsl(var(--onyx)/0.2)] transition-colors"
          >
            <div className="min-w-0">
              <div className="font-medium text-sm truncate">{f.title}</div>
              <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.5)] mt-1">
                {f.attempts_left} of {meta.max_attempts} downloads left
              </div>
            </div>
            <a
              href={`${API}/downloads/${token}/file/${f.key}`}
              data-testid={`download-file-${f.key}`}
              className={`btn-pill !text-xs !py-2 shrink-0 ${f.attempts_left === 0 ? "btn-outline pointer-events-none opacity-50" : "btn-primary"}`}
            >
              <Download size={14} /> Download
            </a>
          </li>
        ))}
      </ul>

      <div className="mt-6 pt-6 border-t border-[hsl(var(--onyx)/0.08)] flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-display text-lg leading-tight">Download all files</div>
          <div className="text-xs text-[hsl(var(--onyx)/0.55)] mt-1">
            {meta.zip.attempts_left} of {meta.max_attempts} zip downloads left
          </div>
        </div>
        <a
          href={zipHref}
          data-testid="download-all-zip"
          className={`btn-pill !text-sm ${meta.zip.attempts_left === 0 ? "btn-outline pointer-events-none opacity-50" : "btn-primary"}`}
        >
          <FileDown size={14} /> Download complete bundle (.zip)
        </a>
      </div>

      <p className="mt-6 text-[11px] leading-relaxed text-[hsl(var(--onyx)/0.5)]">
        Secure links unique to your order · valid for {meta.ttl_days} days · up
        to {meta.max_attempts} downloads per file. A copy of these links has
        also been sent to your email. For your personal or business use only —
        please do not resell, share or redistribute.
      </p>
    </div>
  );
}
